import { useCallback, useMemo, useState } from "react";
import { skipToken } from "@tanstack/react-query";
import { keyBy, mapValues } from "lodash";

import { byDefined } from "../../../../commons/libs/filter-guards";
import { isProductOfType } from "../../../../commons/libs/products";
import { isSizingDataAvailableForBicycleProducts, sizeBicycleProducts } from "../../../../commons/libs/sizing-service";
import { CustomBicycleFilterKey } from "../../../../commons/specs/bicycle";
import { ActiveFilters, FilterTypes, FilterValue, getActiveFilters } from "../../../../commons/specs/filters";
import {
  Product,
  ProductId,
  ProductKey,
  ProductSpecKey,
  ProductType,
  SpecConfig,
  SpecMap
} from "../../../../commons/specs/product";
import { Settings } from "../../../../commons/types/settings";
import { SizingResult } from "../../../../commons/types/sizing";
import { clearActiveFilter, getActiveFilterValues, getProductFiltersFromActiveFilters } from "../../libs/filters";
import useQuery from "../../libs/hooks/use-query";
import { useSettings } from "../../libs/queries/use-settings";
import { UseVeloconnectProductAvailabilitiesReturn } from "../../libs/queries/use-veloconnect-product-availabilities";

export const useBodySizing = <Type extends ProductType>(
  product: Product,
  activeFilters: ActiveFilters<Type>,
  hasInternetConnectivity: boolean
) => {
  const settings = useSettings();

  const isBicycle = isProductOfType(product, ProductType.Bicycle);
  const isPossible = isBicycle && settings.isBodySizingEnabled && hasInternetConnectivity;

  const [activeBodySizingFilterValues] =
    getActiveFilterValues<ProductType.Bicycle, CustomBicycleFilterKey.Sizing>(
      activeFilters as ActiveFilters<ProductType.Bicycle>,
      CustomBicycleFilterKey.Sizing
    ) ?? [];

  const sizingDataAvailable = useQuery({
    queryKey: ["sizing-data-available", product[ProductKey.ProductId], isBicycle, product],
    queryFn: isPossible ? async () => isSizingDataAvailableForBicycleProducts([product]) : skipToken
  });
  const isAvailable = sizingDataAvailable.data?.find(
    sizingStatusBikeResult => sizingStatusBikeResult.productId === product[ProductKey.ProductId]
  )?.isSizable;

  const bodySizing = useQuery({
    queryKey: ["body-sizing", product[ProductKey.ProductId], activeBodySizingFilterValues, isBicycle, product],
    queryFn:
      isPossible && activeBodySizingFilterValues
        ? () => sizeBicycleProducts([product], activeBodySizingFilterValues)
        : skipToken
  });

  // Record of all variants to their sizing result
  const result: Record<ProductId, SizingResult> | null = useMemo(
    () =>
      bodySizing.data
        ? mapValues(
            keyBy(bodySizing.data[0].variants, variant => variant.variantId),
            item => item.result
          )
        : null,
    [bodySizing.data]
  );

  return {
    isPossible,
    isAvailable,
    isLoading: sizingDataAvailable.isLoading || bodySizing.isLoading,
    error: sizingDataAvailable.error ?? bodySizing.error,
    result
  };
};

export const useLocalActiveFilters = <Type extends ProductType>({
  activeFilters,
  settings,
  specConfig
}: {
  settings: Settings;
  activeFilters: ActiveFilters<Type>;
  specConfig: SpecConfig<Type>;
}) => {
  const initialAssortmentFilters = {
    ...(activeFilters?.[ProductSpecKey.CombinedAssortment] !== undefined && {
      combinedAssortment: activeFilters[ProductSpecKey.CombinedAssortment]
    })
  } as ActiveFilters<Type>;

  const [filters, setFilters] = useState<ActiveFilters<Type>>(
    settings.assortment.productFinderFilterSettings.showOnlyAvailableVariants ? initialAssortmentFilters : {}
  );

  type SpecKey = keyof SpecMap[Type];

  /**
   * Remove the old local filter value and add the new one.
   */
  const toggleValue = (specKey: SpecKey, value: FilterValue<Type, keyof SpecMap[Type]>) => {
    setFilters(current => {
      delete current?.[specKey];
      return { ...current, [specKey]: [value] } as ActiveFilters<Type>;
    });
  };

  /**
   * Clears all local filters.
   */
  const clearFilters = () => {
    setFilters({});
  };

  /**
   * Clears a spec key from local filters.
   */
  const clearFilter = (specKey: SpecKey) => {
    setFilters(currentFilters => clearActiveFilter(currentFilters, specKey));
  };

  /**
   * Get the local filter value for a given spec key.
   */
  const getActiveFilterValue = (key: SpecKey) => filters?.[key]?.[0];

  /**
   * The local active filters as a list of ProductFilters.
   *
   * NOTE: If a spec key from the local filter dropdowns is not defined filterConfig,
   * we use a fallback getActiveFilters function with a FilterTypes.Eq filter type.
   * This is okay, because we only use single value selection dropdowns in this view.
   */
  const asProductFilters = getProductFiltersFromActiveFilters(filters, specConfig.filterConfig, filterKey =>
    getActiveFilters<Type>(filterKey, FilterTypes.Eq)
  );

  return {
    filters,
    setFilters,
    getActiveFilterValue,
    asProductFilters,
    toggleValue,
    clearFilter,
    clearFilters
  };
};

export const useVariantCustomizationLink = (productAvailabilities: UseVeloconnectProductAvailabilitiesReturn) => {
  type VariantCustomizationLinkMap = {
    [variantId: string]: string | undefined;
  };

  type VariantCustomizationLink = {
    link: string;
    priority: number;
  };

  const variantCustomizationLinksMap: VariantCustomizationLinkMap = useMemo(() => {
    if (!productAvailabilities.query.data) {
      return {};
    }

    return productAvailabilities.query.data.reduce<VariantCustomizationLinkMap>((acc, productAvailability) => {
      // Gather all customization links from the availabilities
      const customizationLinks: VariantCustomizationLink[] = productAvailability.availabilities
        .map(variantAvailability => {
          const customizationLink = variantAvailability.itemInformation?.bikeCenterDeepLink;
          return !!customizationLink ? { link: customizationLink, priority: variantAvailability.priority } : undefined;
        })
        .filter(byDefined);

      if (customizationLinks.length > 0) {
        // Only add the link with the highest priority to the link map
        const highestPriorityLink = customizationLinks.reduce((highest, current) => {
          return current.priority > highest.priority ? current : highest;
        }).link;

        return {
          ...acc,
          [productAvailability.variantId]: highestPriorityLink
        };
      }

      return acc;
    }, {});
  }, [productAvailabilities.query.data]);

  const showTableHeader = useMemo(() => {
    return Object.values(variantCustomizationLinksMap).length > 0;
  }, [variantCustomizationLinksMap]);

  const isFetching = productAvailabilities.query.isFetching;

  const showButton = !isFetching && showTableHeader;

  const get = useCallback(
    (variantId: string): string | undefined => variantCustomizationLinksMap[variantId],
    [variantCustomizationLinksMap]
  );

  return {
    isFetching,
    showButton,
    showTableHeader,
    get
  };
};
